// Shared helpers for the Stage 2B screens: periods, labels, amounts.
import { useState } from "react";
import { Select } from "./kit/components";
import { T, text } from "./kit/tokens";
import { addDays, bsMonthLabel, fmtAd, fmtBs } from "./bs";
import { DualDateInput } from "./shared";

export const todayIso = () => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

// Plain-language labels (B4). Never "debit"/"credit" outside the accounting preview.
export const KIND = {
    money_in: { label: "Money came in", short: "Money in", dir: "in" },
    money_out: { label: "Money went out", short: "Money out", dir: "out" },
    transfer: { label: "Move money", short: "Moved", dir: "move" },
    bill: { label: "Record a bill", short: "Bill", dir: "none" },
    bill_payment: { label: "Pay a bill", short: "Bill paid", dir: "out" },
    salary_accrual: { label: "Salary owed", short: "Salary owed", dir: "none" },
    salary_payment: { label: "Pay salary owed", short: "Salary paid", dir: "out" },
    staff_cost_owed: { label: "Staff cost owed", short: "Owed", dir: "none" },
    staff_cost_paid_now: { label: "Staff cost paid now", short: "Paid now", dir: "out" },
    staff_cost_payment: { label: "Staff cost payment", short: "Payment", dir: "out" },
    freelancer_paid_now: { label: "Freelancer paid", short: "Freelancer paid", dir: "out" },
    freelancer_bill: { label: "Freelancer bill", short: "Freelancer bill", dir: "none" },
    freelancer_bill_payment: { label: "Freelancer bill payment", short: "Freelancer bill paid", dir: "out" },
    owner_paid_expense: { label: "Paid personally by a director or shareholder", short: "Director paid", dir: "none" },
};
export const whatLabel = (t) => (t?.staff_cost_type ? staffCostLabel(t) : t.purpose_label || KIND[t.kind]?.label || t.kind);

// ─── Stage 2D-1: Staff Costs (manual; nothing comes from Payroll or any operational module) ──
export const STAFF_KINDS = ["staff_cost_owed", "staff_cost_paid_now", "staff_cost_payment"];
export const STAFF_TYPES = [["salary", "Salary"], ["bonus", "Bonus"], ["commission", "Commission"]];
export const STAFF_MODES = [
    ["staff_cost_owed", "Owed, to pay later", "Records the cost now and what the company owes the person. No money moves yet."],
    ["staff_cost_paid_now", "Paid now", "The cost and its payment in one entry, from a bank, cash or wallet account."],
    ["staff_cost_payment", "Pay an amount already owed", "Pays what is owed to this person for this type. Not a new cost."],
];
export const STAFF_PAYABLE_ROLE = { salary: "salaries_payable", bonus: "bonus_payable", commission: "commission_payable" };
export const isStaffCost = (t) => STAFF_KINDS.includes(t?.kind);
const typeName = (type) => (STAFF_TYPES.find(([k]) => k === type) || [null, "Staff cost"])[1];
// "YYYY-MM" (BS) → "Shrawan 2082"; a range → "Shrawan – Bhadra 2082".
export function relatesLabel(from, to) {
    const p = (v) => { const [y, m] = String(v || "").split("-").map(Number); return y && m ? { y, m } : null; };
    const a = p(from), b = p(to) || a;
    if (!a) return "";
    if (a.y === b.y && a.m === b.m) return bsMonthLabel(a.y, a.m);
    return a.y === b.y ? `${bsMonthLabel(a.y, a.m).split(" ")[0]} – ${bsMonthLabel(b.y, b.m)}` : `${bsMonthLabel(a.y, a.m)} – ${bsMonthLabel(b.y, b.m)}`;
}
export function staffCostLabel(t) {
    const what = { staff_cost_owed: "owed", staff_cost_paid_now: "paid", staff_cost_payment: "payment" }[t.kind] || "";
    const rel = relatesLabel(t.relates_from, t.relates_to);
    return `${typeName(t.staff_cost_type)} ${what}${rel ? ` · ${rel}` : ""}`;
}
// What the form sends (the database builds the lines and is the authority on every rule).
export function staffCostPayload(f, kind) {
    const paid = kind !== "staff_cost_owed";
    return {
        kind, staff_cost_type: f.staff_cost_type || null, txn_date: f.txn_date || null, amount: f.amount === "" || f.amount == null ? null : Number(f.amount),
        party_id: f.party_id || null, reference: (f.reference || "").trim() || null, description: (f.description || "").trim() || null,
        operational_ref: (f.operational_ref || "").trim() || null,
        relates_from: f.relates_from || null, relates_to: f.relates_from ? f.relates_to || f.relates_from : null, relates_note: (f.relates_note || "").trim() || null,
        money_account_id: paid ? f.money_account_id || null : null, payment_date: paid ? f.payment_date || null : null,
        payment_method: paid ? f.payment_method || null : null, due_date: paid ? null : f.due_date || null,
    };
}
// The same rules as the database, checked before sending so the form can say what is missing.
export function staffCostProblems(f, kind, today = todayIso()) {
    const p = [];
    if (!STAFF_TYPES.some(([k]) => k === f.staff_cost_type)) p.push("Choose Salary, Bonus or Commission.");
    if (!f.txn_date) p.push("Choose the accounting date.");
    if (!(Number(f.amount) > 0)) p.push("Enter an amount greater than zero.");
    if (!f.party_id) p.push("Choose the staff member.");
    if (!(f.reference || "").trim()) p.push("Enter a reference (payslip, approval or voucher number).");
    if (f.staff_cost_type === "salary" && !f.relates_from) p.push("Choose the month(s) this salary relates to.");
    if (f.relates_from && f.relates_to && f.relates_to < f.relates_from) p.push("\"Relates to\": the first month must not be after the last.");
    if ((f.operational_ref || "").length > 200) p.push("The operational reference is limited to 200 characters.");
    if (kind !== "staff_cost_owed") {
        if (!f.money_account_id) p.push("Choose the bank, cash or wallet account.");
        if (!f.payment_date) p.push("Choose the payment date.");
        else if (f.payment_date > today) p.push("The payment date can't be in the future.");
    }
    return p;
}

export function statusOf(t) {
    if (t.is_reversed) return "reversed";
    if (t.kind === "bill" && t.entry_status === "posted") {
        const out = Number(t.bill_outstanding);
        return out <= 0 ? "paid" : out < Number(t.amount) ? "partpaid" : "unpaid";
    }
    return t.entry_status === "posted" ? "posted" : t.entry_status === "ready" ? "ready" : "draft";
}

export const npr = (n) => `NPR ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Amount with its direction: in (emerald), out (neutral), moved / bill (muted). Struck through when reversed.
export function Amount({ t, size = 14 }) {
    const dir = KIND[t.kind]?.dir;
    const color = t.is_reversed ? T.textFaint : dir === "in" ? T.success : dir === "out" ? T.textStrong : T.textMuted;
    const sign = dir === "in" ? "↓ " : dir === "out" ? "↑ " : dir === "move" ? "⇄ " : "";
    return (
        <span style={{ ...text.money, fontSize: size, color, textDecoration: t.is_reversed ? "line-through" : "none" }}
            title={dir === "in" ? "Money came in" : dir === "out" ? "Money went out" : dir === "move" ? "Moved between company accounts" : "No money moved yet"}>
            {sign}{npr(t.amount)}
        </span>
    );
}

export function DateChip({ iso, cal, display = "BS" }) {
    if (!iso) return <span style={text.caption}>—</span>;
    const b = cal.adToBs(iso);
    const bs = fmtBs(b);
    const first = display === "AD" ? fmtAd(iso) : bs || fmtAd(iso);
    const second = display === "AD" ? bs : bs ? fmtAd(iso) : null;
    return (
        <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.25 }}>
            <span style={{ color: T.textStrong, fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>{first}</span>
            {second && <span style={{ ...text.caption, whiteSpace: "nowrap" }}>{second}</span>}
            {b && !b.verified && <span title="This BS month is not yet verified against the official calendar" style={{ fontSize: 10, color: T.warning, whiteSpace: "nowrap" }}>BS unverified</span>}
        </span>
    );
}

// ─── Period picker: BS month, fiscal year, or a custom BS/AD range ──────────
export function bsMonthRange(cal, iso) {
    const bs = cal.adToBs(iso);
    if (!bs) return null;
    const m = cal.month(bs.y, bs.m);
    return { from: m.ad_start, to: addDays(m.ad_start, m.days - 1), label: bsMonthLabel(bs.y, bs.m) };
}
export function defaultPeriod(cal, years) {
    const today = todayIso();
    const fy = years.find(y => today >= y.start_date && today <= y.end_date);
    if (fy) return { kind: "fy", from: fy.start_date, to: fy.end_date, label: `FY ${fy.label}` };
    const m = bsMonthRange(cal, today);
    if (m) return { kind: "month", ...m };
    return { kind: "custom", from: addDays(today, -30), to: today, label: "Last 30 days" };
}

export function PeriodPicker({ value, onChange, cal, years, display }) {
    const [custom, setCustom] = useState(value.kind === "custom");
    const today = todayIso();
    const options = [];
    const thisMonth = bsMonthRange(cal, today);
    if (thisMonth) {
        options.push(["m0", { kind: "month", ...thisMonth }]);
        const prev = bsMonthRange(cal, addDays(thisMonth.from, -1));
        if (prev) options.push(["m1", { kind: "month", ...prev }]);
    }
    years.slice().reverse().forEach(y => options.push([`fy${y.id}`, { kind: "fy", from: y.start_date, to: y.end_date, label: `FY ${y.label}` }]));
    const current = options.find(([, o]) => o.from === value.from && o.to === value.to)?.[0] || "custom";
    return (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Select aria-label="Period" value={custom ? "custom" : current} style={{ width: "auto", minWidth: 170 }}
                onChange={e => {
                    if (e.target.value === "custom") { setCustom(true); return; }
                    setCustom(false);
                    const o = options.find(([k]) => k === e.target.value);
                    if (o) onChange(o[1]);
                }}>
                {options.map(([k, o]) => <option key={k} value={k}>{o.label}</option>)}
                <option value="custom">Custom dates…</option>
            </Select>
            {custom && (
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 240 }}><span style={text.label}>From</span>
                        <DualDateInput value={value.from} cal={cal} display={display} onChange={v => v && onChange({ kind: "custom", from: v, to: value.to, label: "Custom" })} /></div>
                    <div style={{ minWidth: 240 }}><span style={text.label}>To</span>
                        <DualDateInput value={value.to} cal={cal} display={display} onChange={v => v && onChange({ kind: "custom", from: value.from, to: v, label: "Custom" })} /></div>
                </div>
            )}
        </div>
    );
}

// Accounts that a purpose lets the user choose as its category (mirrors the database rule,
// which remains the authority): active posting accounts under the allowed headings,
// not contra, not payroll-controlled.
const PAYROLL_ROLES = ["salary_expense", "bonus_expense", "commission_expense", "salaries_payable", "bonus_payable", "commission_payable"];
export function categoryOptions(purpose, data) {
    if (!purpose?.choose_parents) return [];
    const byId = Object.fromEntries(data.accounts.map(a => [a.id, a]));
    const payroll = new Set(data.mappings.filter(m => PAYROLL_ROLES.includes(m.role)).map(m => m.account_id));
    const under = (a) => { let p = byId[a.parent_id]; while (p) { if (purpose.choose_parents.includes(p.code)) return true; p = byId[p.parent_id]; } return false; };
    return data.accounts.filter(a => a.active && a.is_postable && !a.is_contra && !payroll.has(a.id) && under(a));
}
export const partyRoleMatch = (roles, role) => role === "owner" ? roles.some(r => r === "shareholder" || r === "director") : roles.includes(role);
// Stage 2D-1 (D3): the six staff-cost accounts are used only through Staff Costs (never in a manual journal).
export const staffCostAccountIds = (data) => new Set((data.mappings || []).filter(m => PAYROLL_ROLES.includes(m.role)).map(m => m.account_id));

// ─── Stage 2D-2: freelancers and director / founder money (manual; nothing comes from any operational module) ──
export const FREELANCER_KINDS = ["freelancer_paid_now", "freelancer_bill", "freelancer_bill_payment"];
export const FREELANCER_MODES = [
    ["freelancer_paid_now", "Paid now", "Work paid immediately from a bank, cash or wallet account."],
    ["freelancer_bill", "Bill to pay later", "Records the cost and what the company owes this freelancer. No money moves yet."],
    ["freelancer_bill_payment", "Pay a freelancer bill", "Pays a posted freelancer bill. Never more than is still owed on it."],
];
export const isFreelancer = (t) => FREELANCER_KINDS.includes(t?.kind);
export const isOwnerPaid = (t) => t?.kind === "owner_paid_expense";
// Staff Costs (2D-1) and the 2D-2 kinds carry a required reference; Review flags them when no evidence file is attached.
export const flagsMissingEvidence = (t) => isStaffCost(t) || isFreelancer(t) || isOwnerPaid(t);
// Categories a director or shareholder may have paid personally (mirrors the database rule, which is the authority):
// active posting expense (5000/6000) or asset (1500) accounts; never contra, party, money or staff-cost accounts.
export function ownerPaidCategories(data) {
    const byId = Object.fromEntries(data.accounts.map(a => [a.id, a]));
    const staffAcc = staffCostAccountIds(data);
    const money = new Set((data.moneyAccounts || []).map(m => m.account_id));
    const under = (a) => { let p = byId[a.parent_id]; while (p) { if (["5000", "6000", "1500"].includes(p.code)) return true; p = byId[p.parent_id]; } return false; };
    return data.accounts.filter(a => a.active && a.is_postable && !a.is_contra && !a.requires_party && !money.has(a.id) && !staffAcc.has(a.id) && under(a))
        .sort((a, b) => a.code.localeCompare(b.code));
}
// The person's OWN loan account (one active posting account under 2410 fixed to them), or null.
export function ownLoanAccount(data, partyId) {
    const h = data.accounts.find(a => a.code === "2410");
    const own = data.accounts.filter(a => a.fixed_party_id === partyId && a.parent_id === h?.id && a.active && a.is_postable);
    return own.length === 1 ? own[0] : null;
}
export function partyPayload(f, kind) {
    const paid = ["freelancer_paid_now", "freelancer_bill_payment"].includes(kind);
    return {
        kind, txn_date: f.txn_date || null, amount: f.amount === "" || f.amount == null ? null : Number(f.amount),
        party_id: kind === "freelancer_bill_payment" ? null : f.party_id || null, bill_id: kind === "freelancer_bill_payment" ? f.bill_id || null : null,
        counter_account_id: kind === "owner_paid_expense" ? f.counter_account_id || null : null,
        reference: (f.reference || "").trim() || null, description: (f.description || "").trim() || null, operational_ref: (f.operational_ref || "").trim() || null,
        relates_from: f.relates_from || null, relates_to: f.relates_from ? f.relates_to || f.relates_from : null, relates_note: (f.relates_note || "").trim() || null,
        money_account_id: paid ? f.money_account_id || null : null, payment_date: paid ? f.payment_date || null : null,
        payment_method: paid ? f.payment_method || null : null, due_date: kind === "freelancer_bill" ? f.due_date || null : null,
    };
}
export function partyProblems(f, kind, today = todayIso()) {
    const p = [];
    if (!f.txn_date) p.push("Choose the accounting date.");
    if (!(Number(f.amount) > 0)) p.push("Enter an amount greater than zero.");
    if (kind === "freelancer_bill_payment") { if (!f.bill_id) p.push("Choose the freelancer bill being paid."); }
    else if (!f.party_id) p.push(kind === "owner_paid_expense" ? "Choose the director or shareholder who paid." : "Choose the freelancer.");
    if (kind === "owner_paid_expense" && !f.counter_account_id) p.push("Choose what was paid for: an expense or an asset.");
    if (!(f.reference || "").trim()) p.push("Enter a reference (bill, invoice, receipt or approval number).");
    if (f.relates_from && f.relates_to && f.relates_to < f.relates_from) p.push("\"Relates to\": the first month must not be after the last.");
    if ((f.operational_ref || "").length > 200) p.push("The operational reference is limited to 200 characters.");
    if (["freelancer_paid_now", "freelancer_bill_payment"].includes(kind)) {
        if (!f.money_account_id) p.push("Choose the bank, cash or wallet account.");
        if (!f.payment_date) p.push("Choose the payment date.");
        else if (f.payment_date > today) p.push("The payment date can't be in the future.");
    }
    return p;
}
